import { Text } from '@thanx/uikit/text'
import { alert } from 'actions/flash'
import { getRedeemTemplates } from 'actions/redeemTemplates'
import {
  useCreatePromotionMutation,
  useGetPromotionQuery,
  useUpdatePromotionMutation,
} from 'api/promotion'
import type {
  CreatePromotionParams,
  DiscountType,
  UpdatePromotionParams,
} from 'api/promotion'
import { getErrorMessage } from 'api/utils'
import ModalContainer from 'components/ModalContainer'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import { Fields as RedeemTemplateFields } from 'models/RedeemTemplate'
import moment from 'moment-timezone'
import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'react-bootstrap-five'
import Helmet from 'react-helmet'
import { useSelector } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import { push } from 'react-router-redux'
import { selectRedeemTemplatesByState } from 'selectors/redeemTemplate'
import { useUserTimeZone } from 'utilities/userTimeZone'
import '../PromoCodes.scss'
import {
  ALL_DAYS_SELECTED,
  DAYS,
  DaySelection,
  hourRange,
  validateTimeRangeDescription,
} from './DayTimePicker'
import FormNavbar from './FormNavbar'
import PromoCodeSection, { CodeType } from './PromoCodeSection'
import RedemptionSection from './RedemptionSection'
import RewardSection from './RewardSection'

const t = buildTranslate('promo_codes.create_edit')

type RouteParams = {
  id?: string
}

function mapTemplateTypeToDiscountType(templateType: string): DiscountType {
  const kind = templateType.split('/').pop()
  switch (kind) {
    case 'item':
      return 'item'
    case 'percent':
      return 'percent'
    case 'fixed_price':
      return 'fixed_price'
    case 'amount':
    default:
      return 'amount'
  }
}

// FixedPrice templates store the value in `price_cap` (the rewards UI
// configures it there); all other types use `discount`. The wizard sends
// a single `discount_value` to the backend, which routes it to the right
// model attribute based on `discount_type`. Exported for unit testing.
export function getTemplateDiscountValue(
  template: RedeemTemplateFields | null
): number {
  if (!template) return 0
  if (template.type === 'manual/fixed_price') return template.price_cap ?? 0
  return template.discount ?? 0
}

// Single source of truth for the reward fields that always belong together
// on save: discount_type, discount_value, template_id, and (for fixed_price)
// discount_scope. Used by buildCreateParams, buildUpdateParams, and the
// draft-save path so a future save site can't silently drop scope.
type RewardParams = {
  discount_type: DiscountType
  discount_value: number
  discount_scope?: string
  template_id?: number
}
export function getRewardParams(
  template: RedeemTemplateFields | null
): RewardParams {
  const params: RewardParams = {
    discount_type: template
      ? mapTemplateTypeToDiscountType(template.type)
      : 'amount',
    discount_value: getTemplateDiscountValue(template),
  }
  if (template?.type === 'manual/fixed_price' && template.discount_scope) {
    params.discount_scope = template.discount_scope
  }
  // The backend links the promo redeem to the chosen template so it inherits
  // the template's product-ID scoping (item-level discounts). Without it, an
  // item-scoped percent template becomes a whole-check discount at checkout.
  if (template?.id) {
    params.template_id = template.id
  }
  return params
}

const CreateEdit: React.FC<RouteComponentProps<RouteParams>> = ({ match }) => {
  const dispatch = useDispatch()
  const promotionId = match.params.id
  const isEdit = Boolean(promotionId)
  const userTimeZone = useUserTimeZone()

  // Convert a datetime-local string to UTC ISO, interpreting it in the user's timezone
  const toUtcIso = (datetimeLocal: string): string =>
    moment.tz(datetimeLocal, userTimeZone).toISOString()

  // Convert a UTC ISO string to datetime-local format in the user's timezone
  const toLocalDatetime = (isoString: string): string =>
    moment.tz(isoString, userTimeZone).format('YYYY-MM-DDTHH:mm')

  // Form state
  const [promoName, setPromoName] = useState('')
  const [isEditingName, setIsEditingName] = useState(false)
  const [codeType, setCodeType] = useState<CodeType>('single')
  const [staticCode, setStaticCode] = useState('')
  const [codeCount, setCodeCount] = useState('100')
  const [selectedTemplate, setSelectedTemplate] =
    useState<RedeemTemplateFields | null>(null)
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [minimumSpend, setMinimumSpend] = useState('')
  const [maximumDiscount, setMaximumDiscount] = useState('')

  // Day-of-week + time-of-day restriction state. Defaults keep the wizard
  // payload-clean until the merchant flips one of the toggles on.
  const [daysEnabled, setDaysEnabled] = useState(false)
  const [days, setDays] = useState<DaySelection>({ ...ALL_DAYS_SELECTED })
  const [timeEnabled, setTimeEnabled] = useState(false)
  const [startHour, setStartHour] = useState(0)
  const [endHour, setEndHour] = useState(23)
  const [timeDescription, setTimeDescription] = useState('')
  // Time zone the restriction's hour arrays are interpreted in. Defaults to
  // the editor's local TZ so a brand-new promo behaves like before — but on
  // edit-mode hydration we copy the promo's persisted
  // `redeemRestrictionTimeZone` here, so a cross-TZ editor (e.g. an LA
  // teammate opening an NY-authored Mon-Thu 11am-2pm promo) doesn't
  // silently rewrite the original TZ on save.
  const [restrictionTimeZone, setRestrictionTimeZone] = useState(userTimeZone)
  // True when hydration sees per-day hour arrays that the wizard's
  // single-range UI cannot represent without silently widening or
  // narrowing them (e.g. Mon=11-14, Wed=15-17). We surface a banner +
  // freeze the day/time toggles so the merchant routes back to
  // Campaign Builder instead of accidentally flattening their setup
  // on save.
  const [advancedRestrictionsLocked, setAdvancedRestrictionsLocked] =
    useState(false)

  // Track whether the form has been modified. Initialised `true` in edit mode
  // so the navbar shows "Save & Exit" — editing an existing promo is always
  // assumed to have changes worth preserving. In create mode, starts `false`
  // so the navbar shows "Cancel" until the user touches any field.
  const [isDirty, setIsDirty] = useState(isEdit)

  // Wrap a state setter so that invoking it flips `isDirty` to true. Used for
  // callbacks passed into child sections; the inline onChange for the promo
  // name input calls setIsDirty directly.
  const markDirty =
    <T,>(setter: (value: T) => void) =>
    (value: T): void => {
      setIsDirty(true)
      setter(value)
    }

  // Derived values
  const parsedCodeCount = parseInt(codeCount, 10)
  const showNameInput = isEditingName || !promoName

  // UI state
  const [showLeaveModal, setShowLeaveModal] = useState(false)
  // Snapshot taken at hydration time: did the loaded promo have ANY
  // day/time restriction data on the BE side? Used to decide whether to
  // pop a "are you sure?" confirm when the merchant tries to save with
  // both toggles flipped off — the BE `apply_restriction` path destroys
  // the row in that case and the per-day arrays are not recoverable.
  const [hadInitialRestriction, setHadInitialRestriction] = useState(false)
  // Confirmation modal for the destructive clear-restrictions save path.
  // Sits in front of `updatePromotion` for the edit + had-restriction +
  // both-toggles-off case; cancel aborts the save with no PATCH.
  const [showClearRestrictionsConfirm, setShowClearRestrictionsConfirm] =
    useState(false)
  // The pending save options captured when the confirm modal opens, so
  // we can resume the same save path (activate vs save-and-exit vs
  // plain submit) after the merchant confirms.
  const [pendingSaveOptions, setPendingSaveOptions] = useState<{
    redirectTo?: string
    activate?: boolean
  } | null>(null)

  // API hooks
  const {
    data: existingPromotion,
    isLoading: isLoadingPromotion,
    isSuccess: isPromotionLoaded,
  } = useGetPromotionQuery(promotionId!, {
    skip: !promotionId,
  })

  const [createPromotion, { isLoading: isCreating }] =
    useCreatePromotionMutation()
  const [updatePromotion, { isLoading: isUpdating }] =
    useUpdatePromotionMutation()

  const isSaving = isCreating || isUpdating

  // Ensure templates are loaded at the parent level so hydration can match them.
  // Filter to 'published' at the API boundary — the selector below also filters
  // to published, and fetching all states pulls in every draft/archived template
  // for the merchant. On sandbox test merchants with accumulated fixtures the
  // unfiltered payload is large enough to time out.
  useEffect(() => {
    dispatch(getRedeemTemplates('published'))
  }, [dispatch])

  // Published reward templates from Redux store
  const templates = useSelector(state =>
    selectRedeemTemplatesByState(state, 'published')
  )

  // Populate form fields for edit mode (runs once when promotion data loads)
  useEffect(() => {
    if (!isPromotionLoaded) return
    const promotion = existingPromotion?.promotion
    if (!promotion) return

    const { pool } = promotion

    setPromoName(promotion.name)

    // Code type
    if (pool?.staticCode) {
      setCodeType('single')
      setStaticCode(pool.staticCode)
    } else if (pool?.generationType === 'single_use') {
      setCodeType('unique')
      setCodeCount(String(pool.totalCodes))
    }

    // Dates (convert UTC to user's timezone for datetime-local input)
    if (promotion.startsAt) setStartsAt(toLocalDatetime(promotion.startsAt))
    if (promotion.endsAt) setEndsAt(toLocalDatetime(promotion.endsAt))

    // Restrictions. Use `!= null` (loose) so a legitimate 0 round-trips
    // through hydration. `if (promotion.minimumSpend)` would skip the
    // assignment for a persisted minimum_spend of 0, leaving the input
    // empty and re-saving the bad value silently — the new $0-block
    // validation only fires when `minimumSpend !== ''`, so it would
    // never catch the already-broken record on edit. Same reasoning
    // applies to maximumDiscount.
    if (promotion.minimumSpend != null)
      setMinimumSpend(String(promotion.minimumSpend))
    if (promotion.maximumDiscount != null)
      setMaximumDiscount(String(promotion.maximumDiscount))

    // Preserve the persisted restriction time zone on edit. Falls back to
    // the editor's local TZ when the promo has none stored (legacy rows
    // or new promos that never enabled the restriction). This matters
    // for cross-TZ editors: without this copy, `buildDayTimeParams` would
    // send the editor's TZ on save and the BE would re-interpret the
    // hour arrays in the wrong zone, silently shifting the window.
    if (promotion.redeemRestrictionTimeZone) {
      setRestrictionTimeZone(promotion.redeemRestrictionTimeZone)
    }

    // Day-of-week / time-of-day restrictions. Older backends omit these
    // fields entirely; default to "off" in that case.
    const dayFields = {
      monday: promotion.redeemRestrictionMonday,
      tuesday: promotion.redeemRestrictionTuesday,
      wednesday: promotion.redeemRestrictionWednesday,
      thursday: promotion.redeemRestrictionThursday,
      friday: promotion.redeemRestrictionFriday,
      saturday: promotion.redeemRestrictionSaturday,
      sunday: promotion.redeemRestrictionSunday,
    }
    const hydratedDays: DaySelection = { ...ALL_DAYS_SELECTED }
    DAYS.forEach(d => {
      const value = dayFields[d]
      if (value !== null && value !== undefined) {
        hydratedDays[d] = value.length > 0
      }
    })
    // Snapshot whether the loaded promo had ANY day/time restriction data
    // configured — used by the destructive-save confirm modal to skip the
    // prompt when a promo had nothing to lose. We treat "time_range true"
    // and "any non-empty per-day hour array" as the load-bearing signals
    // (both presence shapes the BE row carries on a real restriction).
    const anyDayPopulated = DAYS.some(d => (dayFields[d] ?? []).length > 0)
    if (promotion.redeemRestrictionTimeRange || anyDayPopulated) {
      setHadInitialRestriction(true)
    }
    // Only flip the days toggle on when at least one day has been turned
    // off — a fully-on selection is the default and shouldn't count as a
    // narrowing the merchant explicitly opted into.
    const daysAreNarrowed = DAYS.some(d => !hydratedDays[d])
    if (daysAreNarrowed) {
      setDaysEnabled(true)
      setDays(hydratedDays)
    }

    if (promotion.redeemRestrictionTimeRange) {
      // The wizard models the time-of-day restriction as a single
      // [startHour, endHour] window applied to every enabled day. The
      // backend storage is per-day arrays of hours, so a wizard-created
      // promo always has identical, contiguous hour arrays on every
      // enabled day. Promos authored in Campaign Builder can deviate
      // (e.g. Mon=11-14, Wed=15-17 or Mon=[11,13,15]) — collapsing those
      // to a single min/max range would silently widen the restriction
      // on save. Detect that shape and lock the toggles instead.
      const enabledDayHours = DAYS.map(d => dayFields[d] ?? []).filter(
        arr => arr.length > 0
      )
      const isContiguous = (arr: number[]): boolean => {
        for (let i = 1; i < arr.length; i++) {
          if (arr[i] !== arr[i - 1] + 1) return false
        }
        return true
      }
      const arraysIdentical = (a: number[], b: number[]): boolean =>
        a.length === b.length && a.every((v, i) => v === b[i])

      // Days-only encoding signature: `time_range: true` plus per-day
      // arrays that span the full day [0..23] on every enabled day. The
      // save path emits this shape when `daysEnabled && !timeEnabled`
      // (commit 24b68408c — H1 fix). Detect it on hydrate so the time
      // toggle stays off; otherwise re-edit displays a phantom 12 AM-11 PM
      // window the merchant never selected.
      const FULL_DAY_HOURS = Array.from({ length: 24 }, (_, i) => i)
      const isDaysOnlyShape =
        enabledDayHours.length > 0 &&
        enabledDayHours.every(arr => arraysIdentical(arr, FULL_DAY_HOURS))

      if (!isDaysOnlyShape) {
        setTimeEnabled(true)
        const allUniformAndContiguous =
          enabledDayHours.length > 0 &&
          enabledDayHours.every(arr => isContiguous(arr)) &&
          enabledDayHours.every(arr => arraysIdentical(arr, enabledDayHours[0]))

        if (allUniformAndContiguous) {
          const hours = enabledDayHours[0]
          setStartHour(hours[0])
          setEndHour(hours[hours.length - 1])
        } else {
          // Advanced configuration — refuse to round-trip through the wizard.
          // Banner + locked toggles steer the merchant back to Campaign Builder.
          setAdvancedRestrictionsLocked(true)
          const allHours = enabledDayHours.flat()
          if (allHours.length > 0) {
            setStartHour(Math.min(...allHours))
            setEndHour(Math.max(...allHours))
          }
        }
        if (promotion.redeemRestrictionTimeRangeDescription) {
          setTimeDescription(promotion.redeemRestrictionTimeRangeDescription)
        }
      }
    }
  }, [isPromotionLoaded]) // eslint-disable-line react-hooks/exhaustive-deps -- only hydrate once when promotion loads, not on every state change

  // Match reward template separately — depends on templates loading which may
  // happen after the promotion. Only runs if no template is selected yet.
  // FixedPrice templates store the value in `price_cap`, not `discount`,
  // so the comparison field varies by type.
  useEffect(() => {
    if (!isPromotionLoaded || !existingPromotion?.promotion) return
    if (selectedTemplate) return
    if (templates.length === 0) return

    const promotion = existingPromotion.promotion
    const match = templates.find(tmpl => {
      const tmplType = mapTemplateTypeToDiscountType(tmpl.type)
      if (tmplType !== promotion.discountType) return false
      const tmplValue =
        tmpl.type === 'manual/fixed_price' ? tmpl.price_cap : tmpl.discount
      if (tmplValue !== promotion.discountValue) return false
      // FixedPrice templates with the same `price_cap` can still differ by
      // `discount_scope` (e.g. free_base_item vs free_base_item_plus_all_modifiers).
      // Without this guard, hydration silently picks whichever appears first
      // and the wizard would then forward the wrong scope on save.
      //
      // Fall back to value-only match when `promotion.discountScope` is null —
      // legacy promotions (pre-nexus#183) may not have the field persisted, and
      // best-effort match is better than refusing to hydrate any template at all.
      if (tmpl.type === 'manual/fixed_price' && promotion.discountScope) {
        return tmpl.discount_scope === promotion.discountScope
      }
      return true
    })
    if (match) setSelectedTemplate(match)
  }, [templates, isPromotionLoaded]) // eslint-disable-line react-hooks/exhaustive-deps -- selectedTemplate checked inside, existingPromotion stable after load

  // Build the snake_case day/time restriction payload chunk shared by
  // create, update, and save-as-draft. Returns explicit clear signals
  // (time_range: false + empty day arrays) when both toggles are off so
  // the BE destroys any existing restriction. Returns `{}` only in the
  // advanced-locked case where the wizard can't represent the BE's data
  // shape.
  //
  // When the wizard is hydrating a Campaign-Builder-shaped promo (e.g.
  // per-day arrays with different hours per day, or non-contiguous hour
  // ranges), `advancedRestrictionsLocked` is set and the modal disables
  // its toggles + shows a banner directing the merchant to Campaign
  // Builder. Returning {} here is the matching write-side guard: a
  // merchant editing an unrelated field (promo name, etc.) on a locked
  // promo must NOT have the wizard silently flatten the existing per-day
  // arrays to a single min/max range on save. The BE's existing
  // restriction row stays untouched because we send no
  // redeem_restriction_* keys.
  //
  // For the both-toggles-off case we send explicit clear signals
  // (`redeem_restriction_time_range: false` + empty per-day arrays).
  // The BE's `apply_restriction` checks `wants_time_range` and
  // `wants_locations`; with both falsy and no location IDs sent it falls
  // through to `redeem.reload.restriction&.destroy!`, dropping any
  // existing restriction row. We deliberately omit
  // `redeem_restriction_time_zone` so the BE's persisted column value is
  // preserved (a no-op when the row is destroyed, but useful if the
  // merchant re-enables the restriction later).
  const buildDayTimeParams = (): Partial<CreatePromotionParams> => {
    if (advancedRestrictionsLocked) return {}
    if (!daysEnabled && !timeEnabled) {
      // Clear signals only matter in edit mode — they ask the BE to
      // destroy a previously-set restriction row. On create the BE has
      // no row to destroy, so emitting empty per-day arrays + a falsy
      // time_range flag is wasted bytes on every brand-new promo.
      if (!isEdit) return {}
      return {
        redeem_restriction_time_range: false,
        redeem_restriction_monday: [],
        redeem_restriction_tuesday: [],
        redeem_restriction_wednesday: [],
        redeem_restriction_thursday: [],
        redeem_restriction_friday: [],
        redeem_restriction_saturday: [],
        redeem_restriction_sunday: [],
      }
    }
    // When the day toggle is off but the time toggle is on, the merchant
    // expects the time window to apply to every day. Consulting `days[d]`
    // in that case would honour a stale narrowing the user already turned
    // off (e.g. enabled days → deselected Tue-Sun → disabled days). Treat
    // an off `daysEnabled` toggle as "all days selected" instead.
    const effectiveDays: DaySelection = daysEnabled
      ? days
      : { ...ALL_DAYS_SELECTED }
    const hours = timeEnabled ? hourRange(startHour, endHour) : hourRange(0, 23)
    // Any active restriction (days, time, or both) must set
    // `redeem_restriction_time_range: true`. The BE's `apply_restriction`
    // gates on `wants_time_range = source[:redeem_restriction_time_range]
    // == true`; without the flag the entire restriction is treated as
    // "nothing requested" and the BE destroys any existing row — silently
    // discarding the merchant's day selection on a days-only restriction.
    const dayPayload: Partial<CreatePromotionParams> = {
      redeem_restriction_monday: effectiveDays.monday ? hours : [],
      redeem_restriction_tuesday: effectiveDays.tuesday ? hours : [],
      redeem_restriction_wednesday: effectiveDays.wednesday ? hours : [],
      redeem_restriction_thursday: effectiveDays.thursday ? hours : [],
      redeem_restriction_friday: effectiveDays.friday ? hours : [],
      redeem_restriction_saturday: effectiveDays.saturday ? hours : [],
      redeem_restriction_sunday: effectiveDays.sunday ? hours : [],
      // Send the persisted TZ on edits and the editor's local TZ on
      // creates. Hydration above seeds `restrictionTimeZone` from
      // `promotion.redeemRestrictionTimeZone` so an NY-authored promo
      // opened by an LA editor saves back as NY, not LA.
      redeem_restriction_time_zone: restrictionTimeZone,
      redeem_restriction_time_range: true,
    }
    // Only send the customer-facing description when an actual time window
    // is set — TimeRange's `validates :description, presence: true` runs
    // `on: :schedule` (not on the standard save the promo-codes API uses),
    // so a days-only restriction needs no description.
    if (timeEnabled) {
      dayPayload.redeem_restriction_time_range_description =
        timeDescription.trim()
    }
    return dayPayload
  }

  const buildCreateParams = (): CreatePromotionParams => {
    const params: CreatePromotionParams = {
      name: promoName,
      ...getRewardParams(selectedTemplate),
    }

    switch (codeType) {
      case 'single':
        if (staticCode) params.static_code = staticCode
        break
      case 'unique':
        params.code_count = parsedCodeCount
        break
    }

    if (startsAt) params.starts_at = toUtcIso(startsAt)
    if (endsAt) params.ends_at = toUtcIso(endsAt)
    if (minimumSpend) params.minimum_spend = parseFloat(minimumSpend)
    if (maximumDiscount) params.maximum_discount = parseFloat(maximumDiscount)

    if (selectedTemplate?.redemption_venue) {
      params.redemption_venue = selectedTemplate.redemption_venue
    }

    Object.assign(params, buildDayTimeParams())

    return params
  }

  const isDraft = existingPromotion?.promotion?.state === 'draft'

  const buildUpdateParams = (
    activate: boolean = false
  ): UpdatePromotionParams => {
    const params: UpdatePromotionParams = {
      id: promotionId!,
      name: promoName,
    }

    if (isDraft) {
      params.state = activate ? 'active' : 'draft'
    }

    if (selectedTemplate) {
      Object.assign(params, getRewardParams(selectedTemplate))
    }

    if (codeType === 'single' && staticCode) {
      params.static_code = staticCode
    }

    params.starts_at = startsAt ? toUtcIso(startsAt) : null
    params.ends_at = endsAt ? toUtcIso(endsAt) : null
    params.minimum_spend = minimumSpend ? parseFloat(minimumSpend) : null
    params.maximum_discount = maximumDiscount
      ? parseFloat(maximumDiscount)
      : null

    if (selectedTemplate?.redemption_venue) {
      params.redemption_venue = selectedTemplate.redemption_venue
    }

    Object.assign(params, buildDayTimeParams())

    return params
  }

  // Field keys used by inline error rendering across sections. Each field
  // maps to the input that should display the red error message inline,
  // matching the canonical Formsy pattern (see components/Form/Input.tsx).
  type FieldKey =
    | 'name'
    | 'reward'
    | 'staticCode'
    | 'codeCount'
    | 'minimumSpend'
    | 'timeDescription'
    | 'timeRange'
  type FieldErrors = Partial<Record<FieldKey, string>>

  // Single source of truth for form validation. Returns a map of field
  // key → human-readable error message. Order of insertion is preserved
  // for callers that need the first error (e.g. the toast-alert when
  // handleSave finds the form invalid post-submit). Memoized so the
  // three downstream call sites (handleSave, handleSaveAndExit, and the
  // top-level `fieldErrors` derivation) share a single computed result
  // per render — re-runs only when one of the validated inputs changes.
  const fieldErrors = useMemo<FieldErrors>(() => {
    const errors: FieldErrors = {}
    if (!promoName.trim()) {
      errors.name = t('validation.name_required')
    }
    if (!selectedTemplate) {
      errors.reward = t('validation.reward_required')
    }
    if (codeType === 'single' && !staticCode.trim()) {
      errors.staticCode = t('validation.code_required')
    }
    if (
      codeType === 'unique' &&
      (!codeCount || isNaN(parsedCodeCount) || parsedCodeCount <= 0)
    ) {
      errors.codeCount = t('validation.code_count_required')
    } else if (
      codeType === 'unique' &&
      !isNaN(parsedCodeCount) &&
      parsedCodeCount > 4000000
    ) {
      errors.codeCount = t('validation.code_count_max')
    }
    // Minimum spend of $0 is technically valid for the API but never what
    // the merchant intends. The restrictions toggle no longer seeds the
    // field (it opens empty), so this guards a merchant who explicitly
    // types 0 or a negative amount. An enabled-but-empty field stays ''
    // and simply saves no restriction. Block submission on a non-positive
    // entry until the merchant fixes the amount or turns the restriction off.
    if (minimumSpend !== '' && parseFloat(minimumSpend) <= 0) {
      errors.minimumSpend = t('validation.minimum_spend_must_be_positive')
    }
    // When the wizard is hydrating a Campaign-Builder-shaped promo
    // (advancedRestrictionsLocked === true), the modal disables the day +
    // time toggles and buildDayTimeParams returns `{}` — we neither expose
    // the description input for editing nor send a description on save.
    // Validating it here would deadlock the form: a hydrated promo with
    // an empty description (some Campaign Builder rows have one)
    // perma-blocks Save & Exit even though the merchant has no way to
    // edit the field. Skip description + time-range validation entirely
    // in the locked case.
    if (timeEnabled && !advancedRestrictionsLocked) {
      const descriptionLeaf = validateTimeRangeDescription(timeDescription)
      if (descriptionLeaf) {
        // The leaf code (e.g. `time_description_punctuation`) maps to a
        // matching `validation.${leaf}` locale string so the inline error
        // and any future toast surface the same failure-specific copy.
        errors.timeDescription = t(`validation.${descriptionLeaf}`)
      }
      // start === end is a valid one-hour window (e.g. 2:00pm-2:59pm);
      // reject only when end strictly precedes start.
      if (endHour < startHour) {
        errors.timeRange = t('validation.time_end_after_start')
      }
    }
    return errors
  }, [
    promoName,
    selectedTemplate,
    codeType,
    staticCode,
    codeCount,
    parsedCodeCount,
    minimumSpend,
    timeEnabled,
    advancedRestrictionsLocked,
    timeDescription,
    startHour,
    endHour,
  ])

  // Track whether the merchant has attempted to submit, so inline errors
  // surface only after the first submit click — same UX as Formsy's
  // pristine/touched gating in components/Form/Input.tsx.
  const [submitAttempted, setSubmitAttempted] = useState(false)

  // True when saving would destroy the BE's existing restriction row.
  // Conditions: edit mode, the promo loaded with restrictions, and both
  // day + time toggles are now off. We exclude the advanced-locked case
  // because `buildDayTimeParams` returns `{}` in that branch — no clear
  // signal is sent, so the BE row is preserved and there's nothing
  // destructive to confirm.
  const isDestructiveClear =
    isEdit &&
    hadInitialRestriction &&
    !daysEnabled &&
    !timeEnabled &&
    !advancedRestrictionsLocked

  const handleSave = async (options?: {
    redirectTo?: string
    activate?: boolean
  }) => {
    const { redirectTo, activate = false } = options ?? {}

    const errorMessages = Object.values(fieldErrors)
    if (errorMessages.length > 0) {
      // Surface inline errors and flash the first message for accessibility.
      setSubmitAttempted(true)
      dispatch(alert({ key: 'danger', message: errorMessages[0] }))
      return
    }

    // Intercept the destructive clear-restrictions save with a confirm
    // modal. The per-day hour arrays the BE drops are unrecoverable, so
    // we want an explicit ack before the PATCH fires. The merchant can
    // cancel (keep editing) or confirm (proceed via `confirmClearAndSave`,
    // which replays the same options through `executeSave`).
    if (isDestructiveClear) {
      setPendingSaveOptions({ redirectTo, activate })
      setShowClearRestrictionsConfirm(true)
      return
    }

    await executeSave({ redirectTo, activate })
  }

  // The actual PATCH/POST work, split out from `handleSave` so the
  // confirm-modal flow can resume after the merchant says "Yes, clear
  // it". `handleSave` runs validation + the destructive-clear gate;
  // this fn assumes both have already passed.
  const executeSave = async (options: {
    redirectTo?: string
    activate?: boolean
  }) => {
    const { redirectTo, activate = false } = options

    try {
      const result = isEdit
        ? await updatePromotion(buildUpdateParams(activate))
        : await createPromotion(buildCreateParams())

      if ('error' in result) {
        const messageKey = isEdit
          ? 'alerts.update_error'
          : 'alerts.create_error'
        const message = getErrorMessage(result.error, t(messageKey))
        dispatch(alert({ key: 'danger', message }))
        return
      }

      // The flash reducer clears `alerts` on LOCATION_CHANGE, so we push
      // first and raise the toast after — otherwise the navigation wipes
      // the alert before the destination page can render it.
      if (redirectTo) {
        dispatch(push(redirectTo))
        dispatch(alert({ key: 'success', message: t('alerts.update_success') }))
      } else if (isEdit) {
        dispatch(push(`/promo_codes/${promotionId}`))
        dispatch(alert({ key: 'success', message: t('alerts.update_success') }))
      } else if ('data' in result && result.data.promotion) {
        // Unique-code pools kick off an async Sidekiq batch; detail page
        // handles the "generating" state from here. Static pools are ready
        // immediately, so a plain confirmation toast is enough.
        const createdId = result.data.promotion.id
        const toastKey =
          codeType === 'unique'
            ? 'alerts.create_success_unique'
            : 'alerts.create_success_static'
        dispatch(push(`/promo_codes/${createdId}`))
        dispatch(alert({ key: 'success', message: t(toastKey), timeout: 5 }))
      }
    } catch {
      const messageKey = isEdit ? 'alerts.update_error' : 'alerts.create_error'
      dispatch(alert({ key: 'danger', message: t(messageKey) }))
    }
  }

  const handleSubmit = () => handleSave()

  const handleActivate = () => handleSave({ activate: true })

  const handleSaveAndExit = async () => {
    if (Object.keys(fieldErrors).length === 0) {
      await handleSave({ redirectTo: '/promo_codes' })
      return
    }

    // Partial draft save still triggers the destructive-clear gate —
    // dropping a restriction is unrecoverable regardless of which save
    // path the merchant takes. `handleSave` handles its own confirm
    // gate above; the draft path mirrors it here. Using
    // `setPendingSaveOptions({ redirectTo: '/promo_codes' })` is a
    // close-enough signal: when the merchant confirms, we replay the
    // same draft-save through `executeDraftSave`.
    if (isDestructiveClear) {
      setPendingSaveOptions({ redirectTo: '/promo_codes' })
      setShowClearRestrictionsConfirm(true)
      return
    }

    await executeDraftSave()
  }

  // The draft-save PATCH/POST work, split out from `handleSaveAndExit`
  // so the confirm-modal flow can resume after the merchant says "Yes,
  // clear it".
  const executeDraftSave = async () => {
    // Save as draft with whatever data we have. Only send `state: 'draft'`
    // when creating a new promo or editing an existing draft — PATCHing an
    // already-active promo with `state: 'draft'` silently demotes it, which
    // is never what the merchant intended when they cleared a field.
    try {
      const baseParams = {
        name: promoName || t('promo_name.placeholder'),
        ...getRewardParams(selectedTemplate),
        ...(isDraft || !isEdit ? { state: 'draft' as const } : {}),
      }

      // Skip day/time params on the draft path when the time spec is
      // invalid (end < start) — emitting the payload would call
      // hourRange(start, end) which returns [], wiping every day's hours
      // and showing the merchant a default 0/23 window on reload. The
      // partially-typed start/end stays in local React state while the
      // wizard remains open; the user can fix it before truly leaving.
      const timeSpecInvalid = timeEnabled && endHour < startHour
      const dayTimeParams = timeSpecInvalid ? {} : buildDayTimeParams()
      const result = isEdit
        ? await updatePromotion({
            id: promotionId!,
            ...baseParams,
            ...(codeType === 'single' && staticCode
              ? { static_code: staticCode }
              : {}),
            ...(startsAt ? { starts_at: toUtcIso(startsAt) } : {}),
            ...(endsAt ? { ends_at: toUtcIso(endsAt) } : {}),
            ...(minimumSpend
              ? { minimum_spend: parseFloat(minimumSpend) }
              : {}),
            ...(maximumDiscount
              ? { maximum_discount: parseFloat(maximumDiscount) }
              : {}),
            ...dayTimeParams,
          })
        : await createPromotion({
            ...baseParams,
            ...(codeType === 'single' && staticCode
              ? { static_code: staticCode }
              : {}),
            ...(codeType === 'unique' && parsedCodeCount > 0
              ? { code_count: parsedCodeCount }
              : {}),
            ...(startsAt ? { starts_at: toUtcIso(startsAt) } : {}),
            ...(endsAt ? { ends_at: toUtcIso(endsAt) } : {}),
            ...(minimumSpend
              ? { minimum_spend: parseFloat(minimumSpend) }
              : {}),
            ...(maximumDiscount
              ? { maximum_discount: parseFloat(maximumDiscount) }
              : {}),
            ...dayTimeParams,
          })

      if ('error' in result) {
        const message = getErrorMessage(result.error, t('alerts.create_error'))
        dispatch(alert({ key: 'danger', message }))
        return
      }

      dispatch(push('/promo_codes'))
    } catch {
      dispatch(alert({ key: 'danger', message: t('alerts.create_error') }))
    }
  }

  // Confirm-modal handlers for the destructive clear-restrictions path.
  // Cancel just dismisses the modal; the form state (toggles off) is
  // preserved so the merchant can re-toggle or pick a different action.
  // Confirm proceeds with the same save path captured at gate time —
  // either `executeSave` (valid form) or `executeDraftSave` (invalid
  // form, draft path).
  const cancelClearRestrictions = () => {
    setShowClearRestrictionsConfirm(false)
    setPendingSaveOptions(null)
  }
  const confirmClearRestrictions = async () => {
    setShowClearRestrictionsConfirm(false)
    const options = pendingSaveOptions
    setPendingSaveOptions(null)
    if (!options) return
    // When the gate fired from `handleSaveAndExit`'s draft branch we
    // captured `redirectTo: '/promo_codes'` with no `activate` flag,
    // and we still want the draft-save path (not the activate path)
    // because the form was invalid at gate time. We distinguish the
    // two by whether the form is still valid: if it is, run the
    // standard executeSave; if not, run the draft path.
    if (Object.keys(fieldErrors).length === 0) {
      await executeSave(options)
    } else {
      await executeDraftSave()
    }
  }

  const isFormValid = Object.keys(fieldErrors).length === 0
  // Errors only render inline once the merchant has attempted submit —
  // empty until then so the form doesn't open in a "yelling at you" state.
  const visibleFieldErrors: FieldErrors = submitAttempted ? fieldErrors : {}

  const handleSaveAndExitClick = () => {
    if (isFormValid) {
      handleSaveAndExit()
      return
    }
    // Active promos can't be partial-saved-as-draft — surface inline errors
    // and flash the first message instead of opening the leave modal.
    if (isEdit && !isDraft) {
      setSubmitAttempted(true)
      const firstMessage = Object.values(fieldErrors)[0]
      if (firstMessage) {
        dispatch(alert({ key: 'danger', message: firstMessage }))
      }
      return
    }
    setShowLeaveModal(true)
  }

  // Wait for existing promotion to load in edit mode
  if (isEdit && isLoadingPromotion) {
    return null
  }

  return (
    <>
      <Helmet>
        <title>{isEdit ? t('page_title.edit') : t('page_title.create')}</title>
      </Helmet>
      <FormNavbar
        isEdit={isEdit}
        isDraft={isDraft}
        isSaving={isSaving}
        hasChanges={isDirty}
        onCancel={() => dispatch(push('/promo_codes'))}
        onSaveAndExit={handleSaveAndExitClick}
        onSubmit={handleSubmit}
        onActivate={handleActivate}
      />
      <div className="promo-form-container">
        {/* Promo name */}
        <div className="mb-m" data-testid="promo-name-display">
          {showNameInput ? (
            <input
              type="text"
              value={promoName}
              placeholder={t('promo_name.placeholder')}
              onChange={e => {
                if (!isEditingName) setIsEditingName(true)
                setIsDirty(true)
                setPromoName(e.target.value)
              }}
              onBlur={() => {
                if (promoName.trim()) setIsEditingName(false)
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && promoName.trim())
                  setIsEditingName(false)
              }}
              autoFocus
              className="promo-name-input"
              data-testid="promo-name-input"
              aria-label={t('promo_name.placeholder')}
            />
          ) : (
            <div
              className="promo-name-clickable"
              role="button"
              tabIndex={0}
              onClick={() => setIsEditingName(true)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setIsEditingName(true)
                }
              }}
            >
              <Text.Header3
                className={`mt-0 mb-0${
                  !promoName ? ' promo-name-placeholder' : ''
                }`}
              >
                {promoName || t('promo_name.placeholder')}
              </Text.Header3>
            </div>
          )}
          <button
            type="button"
            onClick={() => setIsEditingName(true)}
            className="promo-name-edit-link"
            style={{
              visibility: showNameInput ? 'hidden' : 'visible',
            }}
            tabIndex={showNameInput ? -1 : 0}
          >
            {t('promo_name.edit')}
          </button>
          {visibleFieldErrors.name && (
            <span
              className="text-danger error-message font-size-14 d-block mt-xs"
              data-testid="promo-name-error"
            >
              {visibleFieldErrors.name}
            </span>
          )}
        </div>

        {/* Form sections */}
        <PromoCodeSection
          codeType={codeType}
          onCodeTypeChange={markDirty(setCodeType)}
          staticCode={staticCode}
          onStaticCodeChange={markDirty(setStaticCode)}
          codeCount={codeCount}
          onCodeCountChange={markDirty(setCodeCount)}
          staticCodeError={visibleFieldErrors.staticCode}
          codeCountError={visibleFieldErrors.codeCount}
        />

        <RewardSection
          selectedTemplateId={selectedTemplate?.id ?? null}
          onTemplateSelect={markDirty(setSelectedTemplate)}
          rewardError={visibleFieldErrors.reward}
        />

        <RedemptionSection
          startsAt={startsAt}
          onStartsAtChange={markDirty(setStartsAt)}
          endsAt={endsAt}
          onEndsAtChange={markDirty(setEndsAt)}
          minimumSpend={minimumSpend}
          onMinimumSpendChange={markDirty(setMinimumSpend)}
          maximumDiscount={maximumDiscount}
          onMaximumDiscountChange={markDirty(setMaximumDiscount)}
          minimumSpendError={visibleFieldErrors.minimumSpend}
          daysEnabled={daysEnabled}
          onDaysEnabledChange={markDirty(setDaysEnabled)}
          days={days}
          onDaysChange={markDirty(setDays)}
          timeEnabled={timeEnabled}
          onTimeEnabledChange={markDirty(setTimeEnabled)}
          startHour={startHour}
          onStartHourChange={markDirty(setStartHour)}
          endHour={endHour}
          onEndHourChange={markDirty(setEndHour)}
          timeDescription={timeDescription}
          onTimeDescriptionChange={markDirty(setTimeDescription)}
          advancedRestrictionsLocked={advancedRestrictionsLocked}
        />
      </div>

      <ModalContainer
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        title={t('leave_modal.title')}
        hideCancel
        showBottomProceed={false}
        buttonGroupChildren={
          <>
            <Button
              className="mr-s"
              variant="outline-secondary"
              onClick={() => {
                setShowLeaveModal(false)
                handleSaveAndExit()
              }}
              disabled={isSaving}
            >
              {t('leave_modal.save_and_exit')}
            </Button>
            <Button variant="primary" onClick={() => setShowLeaveModal(false)}>
              {t('leave_modal.finish')}
            </Button>
          </>
        }
      >
        <Text.BodyText3>{t('leave_modal.message')}</Text.BodyText3>
      </ModalContainer>

      {/*
        Confirm modal for the destructive clear-restrictions save path.
        Fires only when isEdit && hadInitialRestriction && both toggles
        off (see isDestructiveClear). The BE drops the entire per-day
        restriction row on this PATCH; per-day arrays are not
        recoverable, so we want an explicit ack before the network
        call. Cancel keeps the merchant on the form with toggles still
        off (they can re-toggle or pick a different action); confirm
        proceeds with the same save path captured at gate time.
      */}
      <ModalContainer
        isOpen={showClearRestrictionsConfirm}
        onClose={cancelClearRestrictions}
        title={t('clear_restrictions_modal.title')}
        hideCancel
        showBottomProceed={false}
        buttonGroupChildren={
          <>
            <Button
              className="mr-s"
              variant="outline-secondary"
              onClick={cancelClearRestrictions}
              disabled={isSaving}
              data-testid="clear-restrictions-cancel"
            >
              {t('clear_restrictions_modal.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={confirmClearRestrictions}
              disabled={isSaving}
              data-testid="clear-restrictions-confirm"
            >
              {t('clear_restrictions_modal.confirm')}
            </Button>
          </>
        }
      >
        <Text.BodyText3>{t('clear_restrictions_modal.message')}</Text.BodyText3>
      </ModalContainer>
    </>
  )
}

export default CreateEdit
